<template>
  <div>
    <div
      class="layout-wrapper layout-static layout-menu-light layout-topbar-blue"
    >
      <div class="layout-topbar">
        <img
          :src="$root.images.beLogoTransparentBackground"
          alt="logo"
          class="layout-topbar-logo"
        />
      </div>
    </div>
    <main>
      <div class="flex justify-content-center">
        <InlineMessage
          v-if="!isTokenValid && messageError"
          severity="error"
          class="mt-3"
        >
          {{ $t(messageError) }}
        </InlineMessage>
        <Skeleton
          v-else-if="!isTokenValid"
          width="360px"
          height="460px"
        ></Skeleton>
        <div v-else class="card shadow-3 w-9" style="padding-top: 70px">
          <h2>{{ askDocumentation.title }}</h2>
          <h3 class="mb-6">{{ askDocumentation.clientName }}</h3>
          <form v-show="!messageSuccess" class="p-fluid formgrid grid">
            <div
              v-for="(documentationType, key) in formObject.documentationTypes"
              :key="key"
              class="field col-12"
            >
              <ClientDocumentationForm
                :documentation-type="documentationType"
                :index="key"
                :uploader="documentationTypeFormUploader"
                :ui="documentationTypeFormUi"
                @upload="handleDocumentationTypeFormUpload"
              />
            </div>

            <template v-if="formObject.inputTypes">
              <div
                v-for="(input, key) in formObject.inputTypes"
                :key="key"
                class="field col-12 mt-3"
              >
                <div v-if="input.id">
                  <div class="line-height-2">
                    <h3 :for="`processInput${input.id}`">{{ input.name }}</h3>
                    <small v-if="input.description" class="ml-1">{{
                      input.description
                    }}</small>
                  </div>
                  <div v-if="input.type === 'upload'">
                    <FileUpload
                      v-if="!input.value"
                      :disabled="input.isLoading"
                      :max-file-size="20000000"
                      :url="$root.api.fileManagerAskDocumentationsSave"
                      :invalid-file-size-message="
                        $t('max_file_size_allowed', { fileSize: '{1}' })
                      "
                      name="files[]"
                      :choose-label="$t('attach')"
                      mode="basic"
                      auto
                      multiple
                      choose-icon="pi pi-paperclip"
                      upload-icon="pi pi-paperclip"
                      @select="onFileSelectIt(key, inputKey, $event)"
                      @before-send="beforeUploadIt(key, inputKey, $event)"
                      @upload="afterUploadIt(key, inputKey, $event)"
                      @error="isLoading = false"
                      @progress="setUploadProgressIt(key, inputKey, $event)"
                    />
                    <FilesUploaded
                      v-if="input.filesUploaded && input.filesUploaded.length"
                      :files-uploaded="input.filesUploaded"
                      class="mb-4 mt-2"
                      :disabled="input.isLoading"
                      @remove-file="removeFileByUrlIt(key, inputKey, $event)"
                    />
                    <InlineMessage
                      v-if="input.n8nErrorMessage"
                      severity="error"
                      class="mt-3"
                    >
                      {{ input.n8nErrorMessage }}
                    </InlineMessage>
                    <InlineMessage
                      v-if="input.n8nSuccessMessage"
                      severity="success"
                      class="mt-3"
                    >
                      {{ input.n8nSuccessMessage }}
                    </InlineMessage>
                  </div>
                </div>
              </div>
            </template>

            <div class="resolution-button-bar mt-4 col-12">
              <Button
                label="Enviar documentação"
                icon="pi pi-envelope"
                class="p-button-outlined w-min"
                autofocus
                :disabled="isLoading"
                @click="submit"
              />
            </div>
            <InlineMessage v-if="messageError" severity="error" class="mt-3">
              {{ $t(messageError) }}
            </InlineMessage>
            <ProgressBar v-if="isLoading" mode="indeterminate" class="mt-2" />
          </form>
          <InlineMessage v-if="messageSuccess" severity="success" class="mt-3">
            {{ $t(messageSuccess) }}
          </InlineMessage>
        </div>
      </div>
    </main>
    <Dialog
      v-model:visible="isLoadingN8n"
      :draggable="false"
      :closable="false"
      :modal="true"
      :dismissable-mask="false"
      class="loading-dialog"
      :style="{ width: '400px' }"
    >
      <template #header>
        <div class="flex align-items-center">
          <i class="pi pi-spin pi-spinner mr-2"></i>
          <span>Aguarde enquanto a informação está a ser recolhida</span>
        </div>
      </template>
      <div
        class="flex flex-column align-items-center justify-content-center p-4"
      >
        <ProgressBar style="width: 50px; height: 50px" mode="indeterminate" />
        <p class="mt-3 text-center">
          Por favor aguarde enquanto a informação do documento é processada.
        </p>
      </div>
    </Dialog>
  </div>
</template>

<script>
import InlineMessage from "primevue/inlinemessage";
import Skeleton from "primevue/skeleton";
import ProgressBar from "primevue/progressbar";
import { ref } from "vue";
import FilesUploaded from "../files/FilesUploaded.vue";
import ClientDocumentationForm from "../clientDocumentations/ClientDocumentationForm.vue";

export default {
  components: {
    InlineMessage,
    Skeleton,
    FilesUploaded,
    ProgressBar,
    ClientDocumentationForm,
  },
  props: { token: { type: String, default: null } },
  setup() {
    const formObject = ref({});
    const askDocumentation = ref(null);
    const isLoading = ref(false);
    const isTokenValid = ref(false);
    const messageError = ref("");
    const messageSuccess = ref("");
    const isLoadingN8n = ref(false);
    return {
      formObject,
      isLoading,
      isTokenValid,
      messageError,
      messageSuccess,
      askDocumentation,
      isLoadingN8n,
    };
  },
  computed: {
    documentationTypeFormUploader() {
      const clientId = this.askDocumentation
        ? this.askDocumentation.clientId
        : "";
      return {
        uploadType: `client_data/${clientId}`,
        documentationUploadUrl: this.$root.api.fileManagerSave,
        inputUploadUrl: this.$root.api.fileManagerAskDocumentationsSave,
        documentationUploaderIdPrefix: "uploadFilesForAskDocumentation",
        inputUploaderIdPrefix: "askDocumentationInputUpload",
      };
    },
    documentationTypeFormUi() {
      return {
        showN8nMessages: true,
        expirationLabel: "Data validade (caso aplicável)",
        expirationPlaceholder: "31/12/2020",
        dropdownPlaceholder: "Selecione",
        inputsWrapperClass: "col-12 p-fluid formgrid grid mt-3",
        inputColumnClass: "field col-12 md:col-6 lg:col-4",
      };
    },
  },
  created() {
    this.tokenConfirmation();
  },
  mounted() {
    if (!this.token) {
      this.$router.push({ name: "login" }).catch(() => {});
    }
  },
  methods: {
    tokenConfirmation() {
      this.messageError = this.messageSuccess = null;
      this.$root
        .apiGet(this.$root.getApiAskDocumentation(this.token))
        .then((response) => {
          this.isTokenValid = true;
          this.askDocumentation = response.data.data;
          this.formObject.documentationTypes = [
            ...response.data.data.documentationTypes,
          ];
          this.formObject.inputTypes = [...response.data.data.inputTypes];
        })
        .catch((error) => {
          this.isTokenValid = false;
          this.messageError = error.response.data.msg_code;
        });
    },
    submit() {
      this.messageError = this.messageSuccess = null;
      this.isLoading = true;
      this.$root.axiosSetAuth(null, false);
      this.persistFiles();
    },
    handleDocumentationTypeFormUpload(event) {
      if (!event || !event.kind) {
        return;
      }
      const key = Number(event.index);
      if (event.kind === "documentation-files-uploaded") {
        this.setDocumentationTypeFiles(key, event.payload);
        return;
      }
      if (event.kind === "documentation-file-removed") {
        this.removeDocumentationTypeFile(key, event.payload);
        return;
      }
      if (event.kind === "documentation-file-selected") {
        this.onFileSelectedDt(key, event.payload);
        return;
      }
      if (event.kind === "documentation-upload-progress") {
        this.setDocumentationTypeUploadProgress(key, event.payload);
        return;
      }
      if (event.kind === "input-files-uploaded") {
        this.setDocumentationTypeInputFiles(key, event.payload);
        return;
      }
      if (event.kind === "input-file-removed") {
        this.removeDocumentationTypeInputFile(key, event.payload);
        return;
      }
      if (event.kind === "upload-error") {
        this.isLoading = false;
      }
    },
    persistFiles() {
      const filesUploaded = [];
      this.formObject.inputTypes.forEach((input) => {
        if (input.filesUploaded) {
          input.filesUploaded.forEach((fileUploaded) => {
            fileUploaded.context = `process/${this.askDocumentation.processId}/inputs`;
            filesUploaded.push(fileUploaded);
          });
        }
      });
      this.formObject.documentationTypes.forEach((documentationType) => {
        if (documentationType.filesUploaded) {
          documentationType.filesUploaded.forEach((fileUploaded) => {
            const targetClientId =
              documentationType.relationClientId ||
              this.askDocumentation.clientId;
            fileUploaded.context = `client_data/${targetClientId}`;
            filesUploaded.push(fileUploaded);
          });
        }
      });
      const parameters = {
        files: filesUploaded,
        clientId: this.askDocumentation.clientId,
      };
      this.$root
        .apiPost(this.$root.api.fileManagerAskDocumentationsPersist, parameters)
        .then((response) => {
          let fileUrlName = null;
          let filePersisted = null;
          this.formObject.inputTypes.forEach((input) => {
            if (input.filesUploaded && input.filesUploaded.length) {
              input.filesUploaded.forEach((fileUploaded) => {
                fileUrlName = fileUploaded.url.substring(
                  fileUploaded.url.lastIndexOf("/") + 1
                );
                filePersisted = response.data.data.find((filePersisted) =>
                  filePersisted.url.endsWith(fileUrlName)
                );
                if (!input.filesPersisted) {
                  input.filesPersisted = [];
                }
                if (filePersisted) {
                  delete input.filesUploaded;
                  input.filesPersisted.push(filePersisted);
                }
              });
            }
          });
          this.formObject.documentationTypes.forEach((documentationType) => {
            console.log(documentationType);
            console.log(documentationType.filesUploaded);
            if (
              documentationType.filesUploaded &&
              documentationType.filesUploaded.length
            ) {
              documentationType.filesUploaded.forEach((fileUploaded) => {
                fileUrlName = fileUploaded.url.substring(
                  fileUploaded.url.lastIndexOf("/") + 1
                );
                filePersisted = response.data.data.find((filePersisted) =>
                  filePersisted.url.endsWith(fileUrlName)
                );
                if (!documentationType.files) {
                  documentationType.files = [];
                }
                if (filePersisted) {
                  delete documentationType.filesUploaded;
                  documentationType.files.push(filePersisted);
                }
              });
            }
          });
          this.updateInputTypes();
        })
        .catch((error) => {
          this.isLoading = false;
          console.log(error);
          console.log(error.response);
          console.log(error.response.data);

          this.$root.toastError(error.response.data.msg_code);
        });
    },
    setDocumentationTypeFiles(key, files) {
      const documentationType = this.formObject.documentationTypes[key];
      if (!documentationType) {
        return;
      }
      documentationType.filesUploaded = files;
    },
    setDocumentationTypeUploadProgress(key, progress) {
      const documentationType = this.formObject.documentationTypes[key];
      if (!documentationType) {
        return;
      }
      documentationType.uploadProgress = progress;
    },
    setDocumentationTypeInputFiles(key, payload) {
      const documentationType = this.formObject.documentationTypes[key];
      if (!documentationType || !Array.isArray(documentationType.inputs)) {
        return;
      }
      const input = documentationType.inputs[payload.inputIndex];
      if (!input) {
        return;
      }
      input.filesUploaded = payload.files;
    },
    removeDocumentationTypeFile(key, url) {
      const documentationType = this.formObject.documentationTypes[key];
      if (!documentationType || !url) {
        return;
      }
      if (
        documentationType.filesUploaded &&
        documentationType.filesUploaded.length
      ) {
        documentationType.filesUploaded =
          documentationType.filesUploaded.filter(
            (file) => file && file.url !== url
          );
      }
      if (documentationType.files && documentationType.files.length) {
        documentationType.files = documentationType.files.filter(
          (file) => file && file.url !== url
        );
      }
    },
    removeDocumentationTypeInputFile(key, payload) {
      const documentationType = this.formObject.documentationTypes[key];
      if (
        !documentationType ||
        !documentationType.inputs ||
        !payload ||
        !payload.url
      ) {
        return;
      }
      const input = documentationType.inputs[payload.inputIndex];
      if (!input) {
        return;
      }
      if (input.filesUploaded && input.filesUploaded.length) {
        input.filesUploaded = input.filesUploaded.filter(
          (file) => file && file.url !== payload.url
        );
      }
      if (input.filesPersisted && input.filesPersisted.length) {
        input.filesPersisted = input.filesPersisted.filter(
          (file) => file && file.url !== payload.url
        );
      }
    },
    updateInputTypes() {
      const parameters = {
        inputTypes: this.formObject.inputTypes,
        documentationTypes: this.formObject.documentationTypes,
      };
      this.$root
        .apiPut(
          this.$root.getApiAskDocumentationInputValues(this.token),
          parameters
        )
        .then(() => {
          this.isLoading = false;
          this.messageSuccess = "A documentação foi enviada";
        })
        .catch((error) => {
          this.isLoading = false;
          this.$root.toastError(error.response.data.msg_code);
        });
    },
    setUploadProgressIt(key, inputKey, event) {
      const input = this.formObject.documentationTypes[key].inputs[inputKey];
      if (input) input.uploadProgress = event.progress;
    },
    beforeUploadIt(key, inputKey, request) {
      const input = this.formObject.documentationTypes[key].inputs[inputKey];
      if (input) input.isLoading = true;
      return request.xhr.setRequestHeader(
        "Authorization",
        this.$root.axiosGetAuth()
      );
    },
    afterUploadIt(key, inputKey, request) {
      const input = this.formObject.documentationTypes[key].inputs[inputKey];
      if (input) input.isLoading = false;
      const files = JSON.parse(request.xhr.response).data;
      files.forEach((file) => {
        file.context = `process/${this.askDocumentation.processId}/inputs`;
        if (!input.filesUploaded) input.filesUploaded = [];
        input.filesUploaded.push(file);
      });
    },
    removeFileByUrlIt(key, inputKey, url) {
      const input = this.formObject.documentationTypes[key].inputs[inputKey];
      if (!input) return;
      if (input.filesUploaded && input.filesUploaded.length) {
        input.filesUploaded = input.filesUploaded.filter(
          (file) => file.url !== url
        );
      }
      if (input.filesPersisted && input.filesPersisted.length) {
        input.filesPersisted = input.filesPersisted.filter(
          (file) => file.url !== url
        );
      }
    },
    onFileSelectedDt(key, selectedFiles) {
      if (!this.$root.api.n8nFileInputs) return;
      if (!selectedFiles || !selectedFiles.length) return;

      const documentationType = this.formObject.documentationTypes[key];
      documentationType.n8nErrorMessage = null;
      documentationType.n8nSuccessMessage = null;

      this.isLoadingN8n = true;

      const formData = new FormData();
      formData.append("file", selectedFiles[0]);

      const inputs = [];
      if (documentationType.inputs) {
        documentationType.inputs.forEach((input) => {
          inputs.push({
            name: input.name,
            type: input.type,
            arrayValues: input.arrayValues || [],
          });
        });
      }
      inputs.push({
        name: "Data validade",
        type: "date",
        arrayValues: [],
      });
      formData.append("inputs", JSON.stringify(inputs));

      const data = {
        clientName:
          documentationType.relationClientName ||
          documentationType.clientName ||
          this.askDocumentation.clientName ||
          null,
        fileName: selectedFiles[0].name,
        processId: this.askDocumentation.processId,
        documentationType: documentationType.baseName || documentationType.name,
      };
      formData.append("data", JSON.stringify(data));

      this.$root
        .apiPost(this.$root.api.n8nFileInputs, formData)
        .then((response) => {
          if (response.data && response.data[0]) {
            Object.entries(response.data[0]).forEach(([name, value]) => {
              if (documentationType.inputs) {
                const input = documentationType.inputs.find(
                  (i) => i.name === name
                );
                if (input) {
                  if (input.type === "number" && typeof value === "string") {
                    input.value = parseFloat(value);
                  } else {
                    input.value = value;
                  }
                }
              }
              if (name === "Data validade" && value) {
                if (typeof value === "string" && value.includes("/")) {
                  const dateParts = value.split("/");
                  if (dateParts.length === 3) {
                    documentationType.expirationDate = new Date(
                      parseInt(dateParts[2], 10),
                      parseInt(dateParts[1], 10) - 1,
                      parseInt(dateParts[0], 10)
                    );
                  }
                } else {
                  documentationType.expirationDate = new Date(value);
                }
              }
            });
            documentationType.n8nSuccessMessage = this.$t(
              "n8n_file_input_success_message"
            );
            setTimeout(() => {
              documentationType.n8nSuccessMessage = null;
            }, 5000);
          }
        })
        .catch(() => {
          documentationType.n8nErrorMessage = this.$t(
            "n8n_file_input_error_message"
          );
          setTimeout(() => {
            documentationType.n8nErrorMessage = null;
          }, 10000);
        })
        .finally(() => {
          this.isLoadingN8n = false;
        });
    },
    onFileSelectIt(key, inputKey, event) {
      if (!this.$root.api.n8nFileInputs) return;
      const selectedFiles = event.files;
      if (!selectedFiles || !selectedFiles.length) return;

      const documentationType = this.formObject.documentationTypes[key];
      const input = documentationType.inputs[inputKey];
      input.n8nErrorMessage = null;
      input.n8nSuccessMessage = null;

      this.isLoadingN8n = true;

      const formData = new FormData();
      formData.append("file", selectedFiles[0]);

      const inputs = [];
      if (documentationType.inputs) {
        documentationType.inputs.forEach((it) => {
          if (it.id && it.type !== "upload") {
            inputs.push({
              name: it.name,
              type: it.type,
              arrayValues: it.arrayValues || [],
            });
          }
        });
      }
      formData.append("inputs", JSON.stringify(inputs));
      const data = {
        clientName: this.askDocumentation.clientName || null,
        fileName: selectedFiles[0].name,
        processId: this.askDocumentation.processId,
        documentationType: input.name || "Input Type",
      };
      formData.append("data", JSON.stringify(data));

      this.$root
        .apiPost(this.$root.api.n8nFileInputs, formData)
        .then((response) => {
          if (response.data && response.data[0]) {
            Object.entries(response.data[0]).forEach(([name, value]) => {
              const matchingInput = documentationType.inputs.find(
                (i) => i.name === name
              );
              if (matchingInput) {
                if (
                  matchingInput.type === "number" &&
                  typeof value === "string"
                ) {
                  matchingInput.value = parseFloat(value);
                } else if (matchingInput.type === "date" && value) {
                  if (typeof value === "string" && value.includes("/")) {
                    const dateParts = value.split("/");
                    if (dateParts.length === 3) {
                      matchingInput.value = new Date(
                        parseInt(dateParts[2], 10),
                        parseInt(dateParts[1], 10) - 1,
                        parseInt(dateParts[0], 10)
                      );
                    } else {
                      matchingInput.value = new Date(value);
                    }
                  } else {
                    matchingInput.value = new Date(value);
                  }
                } else {
                  matchingInput.value = value;
                }
              }
            });
            input.n8nSuccessMessage = this.$t("n8n_file_input_success_message");
            setTimeout(() => {
              input.n8nSuccessMessage = null;
            }, 5000);
          }
        })
        .catch(() => {
          input.n8nErrorMessage = this.$t("n8n_file_input_error_message");
          setTimeout(() => {
            input.n8nErrorMessage = null;
          }, 10000);
        })
        .finally(() => {
          this.isLoadingN8n = false;
        });
    },
  },
};
</script>
